import { useState, useEffect, useCallback } from "react";
import { useAuth, API } from "../App";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Input } from "../components/ui/input";
import { 
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow 
} from "../components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { ScrollArea } from "../components/ui/scroll-area";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { 
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue 
} from "../components/ui/select";
import { 
  Search, RefreshCw, ChevronLeft, ChevronRight, Loader2, 
  Building2, Clock, TrendingUp, RotateCcw, Briefcase, PieChart
} from "lucide-react";
import { toast } from "sonner";

const FundExplorerPage = () => {
  const { token } = useAuth();
  const [funds, setFunds] = useState([]);
  const [loading, setLoading] = useState(true);
  const [total, setTotal] = useState(0);
  const [offset, setOffset] = useState(0);
  const [search, setSearch] = useState("");
  const [selectedFund, setSelectedFund] = useState(null);
  const [fundDetails, setFundDetails] = useState(null);
  const [holdings, setHoldings] = useState([]);
  const [holdingsLoading, setHoldingsLoading] = useState(false);
  const [holdingsTotal, setHoldingsTotal] = useState(0);
  const [holdingsOffset, setHoldingsOffset] = useState(0);
  const [selectedQuarter, setSelectedQuarter] = useState("all");
  const [activeTab, setActiveTab] = useState("quarters");
  
  // Filters
  const [minPortfolioSize, setMinPortfolioSize] = useState("");
  const [minHoldingPeriod, setMinHoldingPeriod] = useState("");
  const [maxHoldingPeriod, setMaxHoldingPeriod] = useState("");
  const [isActiveFilter, setIsActiveFilter] = useState("");
  
  const limit = 25;
  const holdingsLimit = 50;

  const fetchFunds = useCallback(async () => {
    setLoading(true);
    try {
      let url = `${API}/funds/?limit=${limit}&offset=${offset}`;
      if (search) url += `&search=${encodeURIComponent(search)}`;
      if (minPortfolioSize) url += `&min_portfolio_size=${minPortfolioSize}`;
      if (minHoldingPeriod) url += `&min_holding_period=${minHoldingPeriod}`;
      if (isActiveFilter === "active") url += `&is_active=true`;
      if (isActiveFilter === "inactive") url += `&is_active=false`;
      
      const response = await fetch(url, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (response.ok) {
        const data = await response.json();
        setFunds(data.funds || []);
        setTotal(data.total || 0);
      }
    } catch (error) {
      console.error("Failed to fetch funds:", error);
    } finally {
      setLoading(false);
    }
  }, [token, offset, search, minPortfolioSize, minHoldingPeriod, isActiveFilter]);

  useEffect(() => {
    const timer = setTimeout(fetchFunds, search ? 300 : 0);
    return () => clearTimeout(timer);
  }, [fetchFunds]);

  const fetchFundDetails = async (cik) => {
    try {
      const response = await fetch(`${API}/funds/${cik}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (response.ok) {
        const data = await response.json();
        setFundDetails(data);
      }
    } catch (error) {
      console.error("Failed to fetch fund details:", error);
    }
  };

  const fetchHoldings = useCallback(async (cik, quarterFilter = "all") => {
    setHoldingsLoading(true);
    try {
      let url = `${API}/funds/${cik}/holdings?limit=${holdingsLimit}&offset=${holdingsOffset}`;
      if (quarterFilter !== "all") {
        const [year, q] = quarterFilter.split("Q");
        url += `&year=${year}&quarter=${q}`;
      }
      
      const response = await fetch(url, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (response.ok) {
        const data = await response.json();
        setHoldings(data.holdings || []);
        setHoldingsTotal(data.total || 0);
      }
    } catch (error) {
      console.error("Failed to fetch holdings:", error);
    } finally {
      setHoldingsLoading(false);
    }
  }, [token, holdingsOffset]);

  const openFundDetails = (fund) => {
    setSelectedFund(fund);
    setActiveTab("quarters");
    setSelectedQuarter("all");
    setHoldingsOffset(0);
    fetchFundDetails(fund.cik);
  };

  const handleTabChange = (tab) => {
    setActiveTab(tab);
    if (tab === "holdings" && selectedFund) {
      fetchHoldings(selectedFund.cik, selectedQuarter);
    }
  };

  const handleQuarterChange = (quarter) => {
    setSelectedQuarter(quarter);
    setHoldingsOffset(0);
    if (selectedFund) {
      fetchHoldings(selectedFund.cik, quarter);
    }
  };

  useEffect(() => {
    if (activeTab === "holdings" && selectedFund) {
      fetchHoldings(selectedFund.cik, selectedQuarter);
    }
  }, [holdingsOffset]);

  const refreshFund = async (cik) => {
    try {
      const response = await fetch(`${API}/pipeline/refresh-fund/${cik}`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
      });
      if (response.ok) {
        const data = await response.json();
        toast.success(`Reset ${data.reset_count} tasks for fund`);
        fetchFunds();
      }
    } catch (error) {
      toast.error("Failed to refresh fund");
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "—";
    return new Date(dateStr).toLocaleDateString();
  };

  const formatNumber = (num) => {
    if (num === null || num === undefined) return "—";
    return num.toLocaleString();
  };

  const formatValue = (num) => {
    if (num === null || num === undefined) return "—";
    if (num >= 1e9) return `$${(num / 1e9).toFixed(2)}B`;
    if (num >= 1e6) return `$${(num / 1e6).toFixed(2)}M`;
    if (num >= 1e3) return `$${(num / 1e3).toFixed(2)}K`;
    return `$${num.toLocaleString()}`;
  };

  const formatPeriod = (period) => {
    if (period === null || period === undefined) return "—";
    return `${period.toFixed(1)} mo`;
  };

  // Get available quarters from fund details
  const getAvailableQuarters = () => {
    if (!fundDetails?.quarters) return [];
    return fundDetails.quarters
      .filter(q => q.status === "success")
      .map(q => `${q.year}Q${q.quarter}`)
      .sort()
      .reverse();
  };

  return (
    <div className="space-y-6" data-testid="fund-explorer-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold">Fund Explorer</h1>
          <p className="text-muted-foreground text-sm mt-1">
            Browse and manage institutional funds
          </p>
        </div>
      </div>

      {/* Search and Filters */}
      <div className="space-y-4">
        <div className="flex items-center gap-4">
          <div className="relative flex-1 max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input
              placeholder="Search by CIK or name..."
              value={search}
              onChange={(e) => { setSearch(e.target.value); setOffset(0); }}
              className="pl-10 bg-background border-border rounded-sm"
              data-testid="fund-search-input"
            />
          </div>
          <Button variant="outline" onClick={fetchFunds} className="rounded-sm">
            <RefreshCw className="w-4 h-4" />
          </Button>
        </div>

        {/* Advanced Filters */}
        <div className="flex items-center gap-4 flex-wrap">
          <div className="flex items-center gap-2">
            <span className="text-sm text-muted-foreground">Portfolio Size ≥</span>
            <Input
              type="number"
              placeholder="Min"
              value={minPortfolioSize}
              onChange={(e) => { setMinPortfolioSize(e.target.value); setOffset(0); }}
              className="w-24 bg-background border-border rounded-sm text-sm"
              data-testid="min-portfolio-filter"
            />
          </div>
          
          <div className="flex items-center gap-2">
            <span className="text-sm text-muted-foreground">Avg Holding Period ≥</span>
            <Input
              type="number"
              step="1"
              placeholder="Months"
              value={minHoldingPeriod}
              onChange={(e) => { setMinHoldingPeriod(e.target.value); setOffset(0); }}
              className="w-24 bg-background border-border rounded-sm text-sm"
              data-testid="min-holding-period-filter"
            />
            <span className="text-sm text-muted-foreground">mo</span>
          </div>

          <div className="flex items-center gap-2">
            <span className="text-sm text-muted-foreground">Status:</span>
            <select
              value={isActiveFilter}
              onChange={(e) => { setIsActiveFilter(e.target.value); setOffset(0); }}
              className="bg-background border border-border rounded-sm px-3 py-1.5 text-sm"
            >
              <option value="">All</option>
              <option value="active">Active Only</option>
              <option value="inactive">Inactive Only</option>
            </select>
          </div>

          {(minPortfolioSize || minHoldingPeriod || isActiveFilter) && (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => {
                setMinPortfolioSize("");
                setMinHoldingPeriod("");
                setIsActiveFilter("");
                setOffset(0);
              }}
              className="text-muted-foreground"
            >
              Clear Filters
            </Button>
          )}
        </div>
      </div>

      {/* Funds Table */}
      <Card className="bg-card border-border rounded-sm">
        <CardContent className="p-0">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
            </div>
          ) : (
            <Table className="data-table">
              <TableHeader>
                <TableRow>
                  <TableHead>CIK</TableHead>
                  <TableHead>Entity Name</TableHead>
                  <TableHead>Last Filing</TableHead>
                  <TableHead>Portfolio Size</TableHead>
                  <TableHead>Avg Holding Period</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Quarters</TableHead>
                  <TableHead></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {funds.map((fund) => (
                  <TableRow 
                    key={fund.cik} 
                    className="cursor-pointer"
                    onClick={() => openFundDetails(fund)}
                  >
                    <TableCell className="font-mono text-sm">{fund.cik}</TableCell>
                    <TableCell className="max-w-[200px] truncate">
                      {fund.entity_name || fund.investor_name_perf || "—"}
                    </TableCell>
                    <TableCell className="text-sm">{formatDate(fund.last_filing_date)}</TableCell>
                    <TableCell className="font-mono text-sm text-right">
                      {formatNumber(fund.portfolio_size)}
                    </TableCell>
                    <TableCell className="font-mono text-sm text-right">
                      {formatPeriod(fund.average_holding_period)}
                    </TableCell>
                    <TableCell>
                      {fund.is_active ? (
                        <Badge className="status-success rounded-sm">Active</Badge>
                      ) : (
                        <Badge className="status-idle rounded-sm">Inactive</Badge>
                      )}
                    </TableCell>
                    <TableCell className="font-mono text-sm">
                      <span className="text-green-400">{fund.completed_quarters || 0}</span>
                      {" / "}
                      <span className="text-red-400">{fund.failed_quarters || 0}</span>
                      {" / "}
                      <span className="text-muted-foreground">{fund.pending_quarters || 0}</span>
                    </TableCell>
                    <TableCell>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={(e) => { e.stopPropagation(); refreshFund(fund.cik); }}
                        title="Refresh fund"
                        data-testid={`refresh-fund-${fund.cik}`}
                      >
                        <RotateCcw className="w-4 h-4" />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
                {funds.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={8} className="text-center py-8 text-muted-foreground">
                      No funds found
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Pagination */}
      <div className="flex items-center justify-between">
        <p className="text-sm text-muted-foreground">
          Showing {offset + 1} - {Math.min(offset + limit, total)} of {total}
        </p>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => setOffset(Math.max(0, offset - limit))}
            disabled={offset === 0}
            className="rounded-sm"
          >
            <ChevronLeft className="w-4 h-4" />
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => setOffset(offset + limit)}
            disabled={offset + limit >= total}
            className="rounded-sm"
          >
            <ChevronRight className="w-4 h-4" />
          </Button>
        </div>
      </div>

      {/* Fund Details Dialog */}
      <Dialog open={!!selectedFund} onOpenChange={() => { setSelectedFund(null); setFundDetails(null); setHoldings([]); }}>
        <DialogContent className="max-w-5xl max-h-[85vh] overflow-hidden bg-card border-border">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Building2 className="w-5 h-5" />
              {selectedFund?.entity_name || selectedFund?.investor_name_perf || "Fund Details"}
            </DialogTitle>
          </DialogHeader>
          
          {fundDetails && (
            <div className="space-y-4">
              {/* Fund Info */}
              <div className="grid grid-cols-5 gap-4 p-4 bg-muted/30 rounded-sm">
                <div>
                  <p className="label-xs">CIK</p>
                  <p className="font-mono">{fundDetails.cik}</p>
                </div>
                <div>
                  <p className="label-xs">Portfolio Size</p>
                  <p className="font-mono">{formatNumber(fundDetails.portfolio_size)}</p>
                </div>
                <div>
                  <p className="label-xs">Avg Holding Period</p>
                  <p className="font-mono">{formatPeriod(fundDetails.average_holding_period)}</p>
                </div>
                <div>
                  <p className="label-xs">Total Holdings</p>
                  <p className="font-mono">{formatNumber(fundDetails.total_holdings)}</p>
                </div>
                <div>
                  <p className="label-xs">Market Value</p>
                  <p className="font-mono">{formatValue(fundDetails.market_value)}</p>
                </div>
              </div>

              {/* Tabs: Quarters and Holdings */}
              <Tabs value={activeTab} onValueChange={handleTabChange}>
                <TabsList className="bg-muted rounded-sm">
                  <TabsTrigger value="quarters" className="rounded-sm">
                    <Clock className="w-4 h-4 mr-2" />
                    Quarter Status
                  </TabsTrigger>
                  <TabsTrigger value="holdings" className="rounded-sm">
                    <Briefcase className="w-4 h-4 mr-2" />
                    Holdings ({fundDetails.total_holdings || 0})
                  </TabsTrigger>
                </TabsList>

                {/* Quarters Tab */}
                <TabsContent value="quarters" className="mt-4">
                  <ScrollArea className="h-[300px]">
                    <Table className="data-table">
                      <TableHeader>
                        <TableRow>
                          <TableHead>Quarter</TableHead>
                          <TableHead>Status</TableHead>
                          <TableHead>Holdings</TableHead>
                          <TableHead>Retries</TableHead>
                          <TableHead>Last Error</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {fundDetails.quarters?.map((q) => (
                          <TableRow key={`${q.year}-${q.quarter}`}>
                            <TableCell className="font-mono">{q.year} Q{q.quarter}</TableCell>
                            <TableCell>
                              <Badge className={`status-${q.status} rounded-sm`}>{q.status}</Badge>
                            </TableCell>
                            <TableCell className="font-mono">{q.raw_holdings_count || 0}</TableCell>
                            <TableCell className="font-mono">{q.retries || 0}</TableCell>
                            <TableCell className="text-xs text-red-400 max-w-[200px] truncate">
                              {q.last_error || "—"}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </ScrollArea>
                </TabsContent>

                {/* Holdings Tab */}
                <TabsContent value="holdings" className="mt-4">
                  {/* Quarter Filter */}
                  <div className="flex items-center gap-4 mb-4">
                    <div className="flex items-center gap-2">
                      <span className="text-sm text-muted-foreground">Quarter:</span>
                      <Select value={selectedQuarter} onValueChange={handleQuarterChange}>
                        <SelectTrigger className="w-[140px] rounded-sm">
                          <SelectValue placeholder="All Quarters" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="all">All Quarters</SelectItem>
                          {getAvailableQuarters().map((q) => (
                            <SelectItem key={q} value={q}>{q}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <span className="text-sm text-muted-foreground">
                      {holdingsTotal} holdings
                    </span>
                  </div>

                  {holdingsLoading ? (
                    <div className="flex items-center justify-center py-12">
                      <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
                    </div>
                  ) : (
                    <>
                      <ScrollArea className="h-[280px]">
                        <Table className="data-table">
                          <TableHeader>
                            <TableRow>
                              <TableHead>Symbol</TableHead>
                              <TableHead>Company</TableHead>
                              <TableHead>Sector</TableHead>
                              <TableHead>Industry</TableHead>
                              <TableHead className="text-right">Value</TableHead>
                              <TableHead className="text-right">Shares</TableHead>
                              <TableHead>Quarter</TableHead>
                              <TableHead>Filed</TableHead>
                              <TableHead>Accepted</TableHead>
                            </TableRow>
                          </TableHeader>
                          <TableBody>
                            {holdings.map((h, i) => (
                              <TableRow key={i}>
                                <TableCell className="font-mono font-medium text-primary">
                                  {h.cusip_symbol || h.holding_cusip_norm}
                                </TableCell>
                                <TableCell className="max-w-[120px] truncate">
                                  {h.cusip_company_name || "—"}
                                </TableCell>
                                <TableCell>
                                  {h.company_sector ? (
                                    <Badge variant="outline" className="rounded-sm text-xs">
                                      {h.company_sector}
                                    </Badge>
                                  ) : "—"}
                                </TableCell>
                                <TableCell className="text-xs text-muted-foreground max-w-[100px] truncate">
                                  {h.company_industry || "—"}
                                </TableCell>
                                <TableCell className="font-mono text-right text-green-400">
                                  {formatValue(h.holding_value)}
                                </TableCell>
                                <TableCell className="font-mono text-right">
                                  {formatNumber(h.shares)}
                                </TableCell>
                                <TableCell className="font-mono text-xs text-muted-foreground">
                                  {h.year}Q{h.quarter}
                                </TableCell>
                                <TableCell className="text-xs text-muted-foreground">
                                  {h.filing_date ? new Date(h.filing_date).toLocaleDateString() : "—"}
                                </TableCell>
                                <TableCell className="text-xs text-muted-foreground">
                                  {h.accepted_date ? new Date(h.accepted_date).toLocaleDateString() : "—"}
                                </TableCell>
                              </TableRow>
                            ))}
                            {holdings.length === 0 && (
                              <TableRow>
                                <TableCell colSpan={9} className="text-center py-8 text-muted-foreground">
                                  No holdings data available
                                </TableCell>
                              </TableRow>
                            )}
                          </TableBody>
                        </Table>
                      </ScrollArea>

                      {/* Holdings Pagination */}
                      {holdingsTotal > holdingsLimit && (
                        <div className="flex items-center justify-between mt-4 pt-4 border-t border-border">
                          <p className="text-xs text-muted-foreground">
                            Showing {holdingsOffset + 1} - {Math.min(holdingsOffset + holdingsLimit, holdingsTotal)} of {holdingsTotal}
                          </p>
                          <div className="flex items-center gap-2">
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => setHoldingsOffset(Math.max(0, holdingsOffset - holdingsLimit))}
                              disabled={holdingsOffset === 0}
                              className="rounded-sm"
                            >
                              <ChevronLeft className="w-4 h-4" />
                            </Button>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => setHoldingsOffset(holdingsOffset + holdingsLimit)}
                              disabled={holdingsOffset + holdingsLimit >= holdingsTotal}
                              className="rounded-sm"
                            >
                              <ChevronRight className="w-4 h-4" />
                            </Button>
                          </div>
                        </div>
                      )}
                    </>
                  )}
                </TabsContent>
              </Tabs>

              {/* Actions */}
              <div className="flex justify-end gap-2 pt-4 border-t border-border">
                <Button
                  variant="outline"
                  onClick={() => refreshFund(fundDetails.cik)}
                  className="rounded-sm"
                >
                  <RotateCcw className="w-4 h-4 mr-2" />
                  Refresh All Quarters
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default FundExplorerPage;
